import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuthStore, type UserRole } from "@/store/authStore";
import AuthLayout, { AuthDivider, AuthHeading } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Field, PasswordInput } from "@/components/forms";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";

export const Route = createFileRoute("/auth/login/")({
  component: LoginPage,
});

type DashboardPath = "/student/dashboard" | "/partner/dashboard" | "/admin/dashboard" | "/onboarding";

function dashboardFor(role: UserRole | string): DashboardPath {
  if (role === "unassigned") return "/onboarding";
  if (role === "student") return "/student/dashboard";
  if (role === "partner") return "/partner/dashboard";
  return "/admin/dashboard";
}

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const role = (await login(email, password)) as UserRole | "2fa_required";

      if (role === "2fa_required") {
        navigate({ to: "/auth/login-2fa" as const });
        return;
      }

      navigate({ to: dashboardFor(role) });
    } catch (err: unknown) {
      console.error("Login failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (roleEmail: string) => {
    setEmail(roleEmail);
    setPassword("••••••••");
    setLoading(true);
    try {
      const role = (await login(roleEmail, "password")) as UserRole;
      navigate({ to: dashboardFor(role) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthHeading title="Welcome back" description="Sign in to your Taskpart account." />

      <SocialAuthButtons />
      <AuthDivider label="or sign in with email" />

      <form onSubmit={handleLogin} className="space-y-4">
        <Field label="Email address">
          <Input
            type="email"
            autoComplete="email"
            placeholder="name@organization.co.tz"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>

        <Field
          label="Password"
          labelAction={
            <Link
              to="/auth/forgot-password"
              className="text-caption text-muted-foreground transition-colors hover:text-foreground"
            >
              Forgot password?
            </Link>
          }
        >
          <PasswordInput
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        <div className="flex items-center gap-2">
          <Checkbox id="keep-signed-in" />
          <Label htmlFor="keep-signed-in" className="font-normal text-body">
            Keep me signed in
          </Label>
        </div>

        <Button type="submit" className="w-full" size="lg" loading={loading}>
          Sign in
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Don't have an account?{" "}
        <Link to="/auth/register" className="font-medium text-foreground hover:underline">
          Create one
        </Link>
      </p>

      <div className="mt-8 border-t border-border pt-6">
        <p className="eyebrow mb-3 text-center text-[10px]">Quick demo access</p>
        <div className="flex justify-center gap-2">
          {[
            { label: "Student", email: "demo.student@taskpart.org" },
            { label: "Partner", email: "demo.partner@taskpart.org" },
          ].map((role) => (
            <Button
              key={role.label}
              size="sm"
              variant="outline"
              onClick={() => handleDemoLogin(role.email)}
              disabled={loading}
            >
              {role.label} demo
            </Button>
          ))}
        </div>
      </div>
    </AuthLayout>
  );
}
